/**
 * NOLAS 2026, (c) Mikaël FOURNY. Fork de la plateforme TNE 2026.
 * src/components/layout/ReseauxSociaux.tsx
 *
 * Rôle :
 *   « Suivez l'événement » : les liens vers les comptes officiels, Instagram
 *   et la Page LinkedIn. Rendu partout où le public passe (pied de page, et
 *   écran de pré-ouverture tant que le salon n'est pas ouvert).
 *
 * Pourquoi :
 *   Demande de Mika, 2026-09-25. La campagne réseaux tourne depuis le 25/09
 *   (Buffer, compte info.nolas@labriquerie.net, Instagram `nolas_thionville`
 *   et Page LinkedIn `Nolas-Thionville`), et l'application n'offrait AUCUN
 *   chemin vers ces comptes : qui arrive par le site ne les trouve pas.
 *
 * Réutilise :
 *   useSiteSettings() (réglages `social.*`, V101). Aucune adresse écrite en
 *   dur : le socle sert plusieurs événements, et chacun a ses comptes.
 *
 * Sécurité :
 *   `target="_blank"` TOUJOURS avec `rel="noopener noreferrer"` : sans cela
 *   la page de destination reçoit la main sur l'onglet d'origine. Les
 *   adresses sont filtrées en amont (https seulement, cf. useSiteSettings et
 *   _lib/reseaux-sociaux.php, qui applique la même règle côté serveur).
 *
 * Accessibilité :
 *   Chaque lien porte un intitulé lisible (le nom du réseau), pas seulement
 *   un pictogramme. Les icônes sont `aria-hidden` : elles décorent, elles ne
 *   nomment pas.
 *
 * Mikael - chef de projet - 2026-09-25
 */

import { FormattedMessage } from 'react-intl';

import { useEventIdentity } from '@/context/EventIdentityProvider';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import styles from './ReseauxSociaux.module.css';

export interface ReseauxSociauxProps {
  /** `compact` : sans titre, pour un pied de page déjà chargé. */
  readonly variante?: 'complet' | 'compact';
  readonly className?: string;
}

/**
 * Pictogrammes dessinés ici plutôt que chargés : deux fichiers de moins à
 * servir, et aucune dépendance à une bibliothèque d'icônes pour deux formes.
 * `currentColor` : le lien décide de sa couleur, y compris au survol.
 */
function IconeInstagram() {
  return (
    <svg
      className={styles.icone}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="17.6" cy="6.4" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconeLinkedIn() {
  return (
    <svg
      className={styles.icone}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.5" y="2.5" width="19" height="19" rx="3" />
      <line x1="7.2" y1="10.5" x2="7.2" y2="17" />
      <circle cx="7.2" cy="7.1" r="1.2" fill="currentColor" stroke="none" />
      <path d="M11.2 17v-3.6a2.4 2.4 0 0 1 4.8 0V17" />
      <line x1="11.2" y1="10.5" x2="11.2" y2="17" />
    </svg>
  );
}

export function ReseauxSociaux({ variante = 'complet', className }: ReseauxSociauxProps) {
  const { reseauxSociaux } = useSiteSettings();
  // Jamais << salon >> en dur : le socle sert plusieurs types d'evenement.
  const identity = useEventIdentity();

  // Aucun compte déclaré : on n'affiche rien. Une rubrique « Suivez-nous »
  // vide donnerait l'impression d'une page cassée.
  if (reseauxSociaux.instagram === null && reseauxSociaux.linkedin === null) {
    return null;
  }

  return (
    <section
      className={`${styles.bloc} ${variante === 'compact' ? styles.compact : ''} ${className ?? ''}`}
      aria-labelledby="reseaux-sociaux-titre"
    >
      <h2 id="reseaux-sociaux-titre" className={styles.titre}>
        <FormattedMessage id="reseaux.titre" values={identity} />
      </h2>
      <ul className={styles.liste}>
        {reseauxSociaux.instagram !== null && (
          <li>
            <a
              className={styles.lien}
              href={reseauxSociaux.instagram}
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconeInstagram />
              <span>
                <FormattedMessage id="reseaux.instagram" />
              </span>
            </a>
          </li>
        )}
        {reseauxSociaux.linkedin !== null && (
          <li>
            <a
              className={styles.lien}
              href={reseauxSociaux.linkedin}
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconeLinkedIn />
              <span>
                <FormattedMessage id="reseaux.linkedin" />
              </span>
            </a>
          </li>
        )}
      </ul>
    </section>
  );
}

export default ReseauxSociaux;
